import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Check, X, Edit3, Calendar, Instagram, Facebook, Play,
  Loader2, Send, RefreshCw, Sparkles, Film, Clock, Download, Copy, Scissors
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

// mm:ss for clip in/out points
function fmtTime(sec) {
  const t = Math.max(0, Math.round(Number(sec) || 0));
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
}

// Absolute URL of the processed vertical clip for a post, or "" if there isn't one.
// A clip lives at an R2 .mp4 URL or a local /api/clips/ path (the source YouTube/Vimeo embed is NOT the clip).
function clipUrl(post) {
  const u = post?.video_url || "";
  if (u.includes("r2.dev") || u.endsWith(".mp4")) {
    return u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
  }
  if (u.includes("/api/clips/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`;
  return "";
}

// Instagram phone mockup
function InstagramPreview({ post }) {
  return (
    <div className="w-full max-w-[320px] mx-auto">
      <div className="bg-black rounded-2xl border border-[#363b45] overflow-hidden">
        {/* IG header */}
        <div className="flex items-center gap-2.5 px-3 py-2.5 border-b border-[#222630]">
          <div className="w-7 h-7 rounded-full" style={{ background: post.brand?.color_hex || "#c6a96f" }} />
          <span className="text-xs font-semibold text-zinc-200">{post.brand?.name?.toLowerCase().replace(/\s+/g, "") || "brand"}</span>
        </div>
        {/* Media — the processed vertical clip if we have it */}
        <div className="aspect-square bg-studio-surface">
          {clipUrl(post) ? (
            <video
              src={`${clipUrl(post)}#t=0.5`}
              className="w-full h-full object-cover"
              muted preload="metadata"
            />
          ) : post.thumbnail_url ? (
            <img src={post.thumbnail_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${post.thumbnail_url}` : post.thumbnail_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center"><Film size={32} className="text-zinc-600" /></div>
          )}
        </div>
        {/* Actions row */}
        <div className="flex items-center gap-4 px-3 py-2">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e4e4e7" strokeWidth="2"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e4e4e7" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#e4e4e7" strokeWidth="2"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/></svg>
        </div>
        {/* Caption */}
        <div className="px-3 pb-3">
          <p className="text-xs text-zinc-200 leading-relaxed">
            <span className="font-semibold">{post.brand?.name?.toLowerCase().replace(/\s+/g, "") || "brand"}</span>{" "}
            {post.caption}
          </p>
          {post.hashtags?.length > 0 && (
            <p className="text-xs text-[#5b8ec4] mt-1">{post.hashtags.slice(0, 6).join(" ")}</p>
          )}
        </div>
      </div>
    </div>
  );
}

function PreviewPanel({ post, onClose, onApprove, onReject, onPublishNow, onSaveCaption, onRegenerateCaption, onPickVariant, onRevise }) {
  const [caption, setCaption] = useState(post?.caption || "");
  const [revNotes, setRevNotes] = useState("");
  const [revShot, setRevShot] = useState(null);   // {dataUrl, mime, name}
  const [revBusy, setRevBusy] = useState(false);
  const [options, setOptions] = useState(post?.caption_options || []);
  const [angle, setAngle] = useState(post?.caption_angle || null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [actionLoading, setActionLoading] = useState("");

  if (!post) return null;

  const isCarousel = post.post_kind === "carousel" && (post.image_urls?.length || 0) > 0;
  const isVimeo = post.content_item_source === "vimeo" || post.video_url?.includes("vimeo");
  const isYoutube = post.content_item_source === "youtube";
  const vimeoId = post.content_item_source_id?.split("/").pop() || "";
  const youtubeId = post.content_item_source_id || "";
  const scheduled = post.scheduled_at ? new Date(post.scheduled_at) : null;
  // The processed vertical clip (R2 .mp4 or local /api/clips/) is the deliverable — prefer it.
  const clipSrc = clipUrl(post);
  const hasClip = !!clipSrc;

  const handleSave = async () => {
    setSaving(true);
    await onSaveCaption(post.id, caption);
    setSaving(false);
    setEditing(false);
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    const result = await onRegenerateCaption(post);
    if (result?.caption) {
      setCaption(result.caption);
      setOptions(result.options || []);
      setAngle(result.options?.[0]?.angle || null);
    }
    setRegenerating(false);
  };

  const handlePick = async (opt) => {
    setCaption(opt.text);
    setAngle(opt.angle);
    await onPickVariant(post.id, opt);
  };

  const handleApprove = async () => {
    setActionLoading("approve");
    await onApprove(post.id);
    setActionLoading("");
  };

  const handlePublishNow = async () => {
    setActionLoading("publish");
    await onPublishNow(post.id);
    setActionLoading("");
  };

  const handleReject = async () => {
    setActionLoading("reject");
    await onReject(post.id);
    setActionLoading("");
  };

  const handleShotPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Screenshot too large (max 8MB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setRevShot({ dataUrl: reader.result, mime: file.type, name: file.name });
    reader.readAsDataURL(file);
  };

  const handleRevise = async () => {
    if (revNotes.trim().length < 5) { toast.error("Describe what's wrong first"); return; }
    setRevBusy(true);
    const b64 = revShot ? revShot.dataUrl.split(",")[1] : null;
    const ok = await onRevise(post.id, revNotes.trim(), b64, revShot?.mime || null);
    setRevBusy(false);
    if (ok) { setRevNotes(""); setRevShot(null); }
  };

  const isRevising = post.status === "revising";
  const revisions = (post.revisions || []).slice().reverse();

  return (
    <div className="fixed top-[32px] right-[340px] w-[440px] h-[calc(100vh-32px)] bg-[#0f1114] border-l border-[#222630] z-[48] flex flex-col overflow-y-auto animate-slideIn"
      data-testid="preview-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#222630] flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full" style={{ background: post.brand?.color_hex || "#71717a" }} />
          <span className="text-sm font-medium text-zinc-200">{post.brand?.name}</span>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-[#21252b] transition-all" data-testid="preview-close">
          <X size={15} />
        </button>
      </div>

      {/* Carousel posts: the stills strip IS the content — no video player */}
      {isCarousel ? (
        <div className="p-4 border-b border-[#222630]">
          <p className="section-label mb-2">// carousel · {post.image_urls.length} frames</p>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {post.image_urls.map((url, i) => (
              <img key={i} src={url} alt={`frame ${i + 1}`}
                className="h-44 rounded-lg border border-[#2a2e36] flex-shrink-0 object-cover" />
            ))}
          </div>
        </div>
      ) : (
      <>
      {/* Video player — show the PROCESSED VERTICAL CLIP (what actually gets posted), not the
          full-length YouTube/Vimeo source. The source embed is only a fallback for unprocessed items. */}
      <div className="aspect-[9/16] max-h-[60vh] bg-black flex-shrink-0 flex items-center justify-center">
        {hasClip ? (
          <video
            src={clipSrc}
            controls autoPlay loop
            className="w-full h-full object-contain bg-black"
            data-testid="preview-video-player"
            ref={(el) => {
              // Sound ON by default — the panel opens from a real click, so browsers allow it.
              // If a stricter autoplay policy still blocks it, fall back to muted rather than
              // showing a frozen player.
              if (el) el.play().catch(() => { el.muted = true; el.play().catch(() => {}); });
            }}
          />
        ) : isVimeo && vimeoId && Number(vimeoId) > 200000 ? (
          <iframe src={`https://player.vimeo.com/video/${vimeoId}?autoplay=0&title=0&byline=0&portrait=0`}
            className="w-full h-full" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen title="Preview" />
        ) : isYoutube && youtubeId ? (
          <iframe src={`https://www.youtube.com/embed/${youtubeId}`}
            className="w-full h-full" frameBorder="0" allow="autoplay; fullscreen" allowFullScreen title="Preview" />
        ) : post.thumbnail_url ? (
          <img src={post.thumbnail_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${post.thumbnail_url}` : post.thumbnail_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Film size={32} className="text-zinc-600" /></div>
        )}
      </div>
      {!hasClip && (
        <div className="px-4 py-2 bg-[#1a1d21] text-[11px] text-rose-300 font-mono">
          No processed clip yet — this shows the source video. Process it to generate the vertical cut.
        </div>
      )}
      </>
      )}

      {/* Request changes — notes (+ screenshot) → surgical trim or guided full re-cut */}
      {(hasClip || isRevising) && (
        <div className="p-4 border-b border-[#222630]">
          <p className="section-label mb-2">// request changes</p>
          {isRevising ? (
            <div className="rounded-xl bg-gold/5 border border-gold/20 px-3 py-3 flex items-center gap-2.5">
              <Loader2 size={14} className="animate-spin text-gold flex-shrink-0" />
              <div>
                <p className="text-xs text-gold">Revision in progress</p>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Small fixes take ~1 min; a full re-cut takes a few. You'll get a notification here the moment it's done.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <textarea value={revNotes} onChange={(e) => setRevNotes(e.target.value)}
                placeholder={'What’s wrong? Be specific.\n• "Remove the shaky frames right after the kiss" (pause on them and attach a screenshot)\n• "Wrong moment — use the vows instead of the dancing"'}
                className="w-full h-24 bg-studio-surface border border-[#2a2e36] rounded-xl p-3 text-xs text-zinc-200 outline-none focus:border-gold-dim resize-none placeholder:text-zinc-600"
                data-testid="revision-notes" />
              <div className="flex items-center gap-2">
                <label className="text-[11px] px-2.5 py-1.5 rounded-lg border border-[#2a2e36] text-zinc-400 hover:text-zinc-200 hover:border-zinc-500 cursor-pointer transition-colors flex items-center gap-1.5">
                  <Copy size={11} />
                  {revShot ? "Change screenshot" : "Attach screenshot of the moment"}
                  <input type="file" accept="image/*" className="hidden" onChange={handleShotPick} data-testid="revision-screenshot" />
                </label>
                {revShot && (
                  <span className="flex items-center gap-1.5 min-w-0">
                    <img src={revShot.dataUrl} alt="" className="h-8 rounded border border-[#2a2e36]" />
                    <button onClick={() => setRevShot(null)} className="text-zinc-600 hover:text-zinc-400"><X size={12} /></button>
                  </span>
                )}
                <button onClick={handleRevise} disabled={revBusy || revNotes.trim().length < 5}
                  className="ml-auto btn-gold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1.5 disabled:opacity-40"
                  data-testid="revision-submit">
                  {revBusy ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                  Fix It
                </button>
              </div>
              <p className="text-[10px] text-zinc-600 leading-relaxed">
                Specific notes (bad frames, one shot) get a surgical edit of this exact clip.
                Big notes (wrong moment, different vibe) trigger a full re-cut guided by what you wrote.
              </p>
            </div>
          )}
          {revisions.length > 0 && (
            <div className="mt-3 space-y-1.5">
              {revisions.slice(0, 4).map((r) => (
                <div key={r.id} className="rounded-lg bg-studio-surface border border-[#222630] px-2.5 py-2">
                  <p className="text-[11px] text-zinc-400 leading-snug">"{r.notes}"</p>
                  <p className={`text-[10px] font-mono mt-1 ${r.status === "done" ? "text-status-green"
                    : r.status === "failed" ? "text-rose-300" : "text-gold"}`}>
                    {r.status === "in_progress" ? "working on it…"
                      : `${r.status === "done" ? "done" : "failed"}${r.mode ? ` · ${r.mode === "trim" ? "surgical trim" : "full re-cut"}` : ""} — ${r.summary || ""}`}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* What the AI cut, and why */}
      {hasClip && (
        <div className="p-4 border-b border-[#222630]">
          <p className="section-label mb-2">// ai edit</p>
          <div className="flex items-center gap-2 flex-wrap text-[11px] font-mono text-zinc-400 mb-2">
            <span className="px-1.5 py-0.5 rounded bg-teal/10 text-teal">
              {post.clip_format === "smart_crop" ? "9:16 crop" : "letterbox"}
            </span>
            {post.clip_duration ? <span>{Math.round(post.clip_duration)}s</span> : null}
            {post.clip_segment && (
              <span className="text-zinc-500">
                cut {fmtTime(post.clip_segment.start)}–{fmtTime(post.clip_segment.end)}
                {post.source_duration ? ` of ${fmtTime(post.source_duration)}` : ""}
              </span>
            )}
            {post.clip_picked_by && (
              <span className={`px-1.5 py-0.5 rounded ${post.clip_picked_by === "video"
                ? "bg-status-green/10 text-status-green"
                : post.clip_picked_by === "fallback" ? "bg-rose-500/10 text-rose-300" : "bg-gold/10 text-gold"}`}
                title={post.clip_picked_by === "video" ? "Gemini watched and listened to the whole film"
                  : post.clip_picked_by === "frames" ? "Chosen from sampled stills (no audio)"
                  : "AI analysis unavailable — middle of the film"}>
                {post.clip_picked_by === "video" ? "watched film" : post.clip_picked_by === "frames" ? "stills only" : "fallback"}
              </span>
            )}
          </div>
          {post.clip_reason && (
            <p className="text-xs text-zinc-400 leading-relaxed italic">{post.clip_reason}</p>
          )}
          {(post.clip_hook || post.clip_subtitle_count > 0 || post.clip_background === "blur") && (
            <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono text-zinc-500 mt-2">
              {post.clip_hook && (
                <span className="px-1.5 py-0.5 rounded bg-gold/10 text-gold"
                  title={`Cold open: ${fmtTime(post.clip_hook.start)}–${fmtTime(post.clip_hook.end)}${post.clip_hook.reason ? " — " + post.clip_hook.reason : ""}`}>
                  hook open
                </span>
              )}
              {post.clip_subtitle_count > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-teal/10 text-teal" title="Timed speech subtitles burned into the clip">
                  {post.clip_subtitle_count} subtitle{post.clip_subtitle_count === 1 ? "" : "s"}
                </span>
              )}
              {post.clip_background === "blur" && <span className="px-1.5 py-0.5 rounded bg-studio-surface">blur fill</span>}
            </div>
          )}
        </div>
      )}

      {/* Instagram preview mockup */}
      <div className="p-4 border-b border-[#222630]">
        <p className="section-label mb-3">// instagram preview</p>
        <InstagramPreview post={{ ...post, caption }} />
      </div>

      {/* Caption editor */}
      <div className="p-4 border-b border-[#222630]">
        <div className="flex items-center justify-between mb-2">
          <p className="section-label">// caption{angle ? ` · ${angle}` : ""}</p>
          <div className="flex gap-1.5">
            <button onClick={handleRegenerate} disabled={regenerating}
              className="text-[11px] px-2.5 py-1 rounded-full border border-gold/30 text-gold hover:bg-gold/5 transition-colors flex items-center gap-1 disabled:opacity-40"
              data-testid="regenerate-caption-btn">
              {regenerating ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
              Regenerate
            </button>
            {!editing && (
              <button onClick={() => setEditing(true)}
                className="text-[11px] px-2.5 py-1 rounded-full border border-[#363b45] text-zinc-400 hover:text-zinc-300 transition-colors flex items-center gap-1">
                <Edit3 size={10} /> Edit
              </button>
            )}
          </div>
        </div>
        {editing ? (
          <div className="space-y-2">
            <textarea value={caption} onChange={(e) => setCaption(e.target.value)}
              className="w-full h-28 bg-studio-surface border border-[#2a2e36] rounded-xl p-3 text-sm text-zinc-200 outline-none focus:border-gold-dim resize-none"
              data-testid="preview-caption-editor" />
            <div className="flex gap-2">
              <button onClick={handleSave} disabled={saving} className="btn-gold px-3 py-1.5 rounded-lg text-xs flex items-center gap-1">
                {saving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />} Save
              </button>
              <button onClick={() => { setEditing(false); setCaption(post.caption); }} className="px-3 py-1.5 rounded-lg text-xs border border-[#363b45] text-zinc-400">Cancel</button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-zinc-300 leading-relaxed">{caption}</p>
        )}
        {!editing && options.length > 1 && (
          <div className="mt-3 space-y-1.5">
            <p className="text-[10px] font-mono uppercase text-zinc-600 tracking-wider">variants</p>
            {options.map((opt) => {
              const active = caption === opt.text;
              return (
                <button key={opt.angle} onClick={() => handlePick(opt)}
                  className={`w-full text-left rounded-lg border px-2.5 py-2 transition-colors ${active
                    ? "border-gold/50 bg-gold/5"
                    : "border-[#2a2e36] hover:border-[#363b45] bg-studio-surface"}`}
                  data-testid={`caption-variant-${opt.angle}`}>
                  <span className={`text-[9px] font-mono uppercase tracking-wider ${active ? "text-gold" : "text-zinc-500"}`}>
                    {opt.angle}{active ? " · selected" : ""}
                  </span>
                  <span className="block text-xs text-zinc-300 leading-snug mt-0.5">{opt.text}</span>
                </button>
              );
            })}
          </div>
        )}
        {post.hashtags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {post.hashtags.map((tag) => <span key={tag} className="text-[10px] text-teal font-mono">{tag}</span>)}
          </div>
        )}
      </div>

      {/* Meta */}
      <div className="p-4 border-b border-[#222630]">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div><span className="text-zinc-500">Platforms</span>
            <div className="flex gap-2 mt-1">
              {post.platforms?.includes("instagram") && <span className="flex items-center gap-1 text-[#c47272]"><Instagram size={12} /> Instagram</span>}
              {post.platforms?.includes("facebook") && <span className="flex items-center gap-1 text-[#5b8ec4]"><Facebook size={12} /> Facebook</span>}
            </div>
          </div>
          <div><span className="text-zinc-500">Posts (scheduled)</span>
            <p className="text-zinc-200 mt-1 font-mono text-[11px]">
              {scheduled && scheduled <= new Date() && (post.status === "pending" || post.status === "pending_review")
                ? "next open slot on approve"
                : scheduled ? scheduled.toLocaleString() : "Next open slot on approve"}
            </p>
          </div>
          {(post.clip_processed_at || post.created_at) && (
            <div><span className="text-zinc-500">Cut created</span>
              <p className="text-zinc-200 mt-1 font-mono text-[11px]">
                {new Date(post.clip_processed_at || post.created_at).toLocaleString()}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="p-4 flex flex-col gap-2 flex-shrink-0">
        {/* Download + Copy row */}
        <div className="flex gap-2">
          <button onClick={async () => {
            try {
              // Fetch through the backend (carries auth + avoids the R2 bucket's missing CORS headers).
              const isCarousel = (post.content_plan?.type || (post.content_item_source === "carousel" ? "carousel" : "video")) === "carousel";
              const endpoint = isCarousel ? `/posts/${post.id}/download-zip` : `/posts/${post.id}/download-file`;
              const filename = isCarousel
                ? `${post.brand?.short_name || "post"}_carousel.zip`
                : `${(post.video_title || post.brand?.short_name || "reel").replace(/[^a-z0-9]+/gi, "_").slice(0, 50)}.mp4`;
              const resp = await api.get(endpoint, { responseType: "blob" });
              const blobUrl = URL.createObjectURL(resp.data);
              const a = document.createElement("a");
              a.href = blobUrl;
              a.download = filename;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(blobUrl);
            } catch { toast.error("Download failed"); }
          }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border border-[#363b45] text-zinc-400 text-xs hover:text-gold hover:border-gold/30 transition-colors"
            data-testid="preview-download-btn">
            <Download size={14} /> Download
          </button>
          <button onClick={() => {
            const text = (post.caption || "") + "\n\n" + (post.hashtags || []).join(" ");
            navigator.clipboard.writeText(text);
            toast.success("Caption copied to clipboard");
          }}
            className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border border-[#363b45] text-zinc-400 text-xs hover:text-gold hover:border-gold/30 transition-colors"
            data-testid="preview-copy-caption-btn">
            <Copy size={14} /> Copy Caption
          </button>
        </div>

        <div className="flex gap-2">
          <button onClick={handleApprove} disabled={!!actionLoading}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl bg-status-green/10 text-status-green text-sm font-medium hover:bg-status-green/20 transition-colors disabled:opacity-40"
            data-testid="preview-approve-btn">
            {actionLoading === "approve" ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Approve & Schedule
          </button>
          <button onClick={handlePublishNow} disabled={!!actionLoading}
            className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl btn-gold text-sm font-medium disabled:opacity-40"
            data-testid="preview-publish-now-btn">
            {actionLoading === "publish" ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Post Now
          </button>
        </div>
        <button onClick={handleReject} disabled={!!actionLoading}
          className="w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-status-red/30 text-status-red text-xs hover:bg-status-red/10 transition-colors disabled:opacity-40"
          data-testid="preview-reject-btn">
          {actionLoading === "reject" ? <Loader2 size={14} className="animate-spin" /> : <X size={14} />}
          Reject & Delete
        </button>
      </div>
    </div>
  );
}

function PostCard({ post, onClick, onApprove }) {
  const [approving, setApproving] = useState(false);
  const scheduled = post.scheduled_at ? new Date(post.scheduled_at) : null;
  const isPendingStatus = post.status === "pending" || post.status === "pending_review";
  const slotStale = isPendingStatus && scheduled && scheduled <= new Date();
  // When the AI edit itself was made (re-cuts move this; the post may be older)
  const cutAt = post.clip_processed_at ? new Date(post.clip_processed_at)
    : post.created_at ? new Date(post.created_at) : null;
  const fmtDT = (d) => d.toLocaleDateString([], { month: "short", day: "numeric" }) + " " +
    d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  const handleCardApprove = async (e) => {
    e.stopPropagation();
    setApproving(true);
    await onApprove(post.id);
    setApproving(false);
  };
  const source = post.content_item_source || (post.content_plan ? "ai" : "");
  const contentType = post.post_kind === "carousel" ? "carousel" : (post.content_plan?.type || "video");
  const sourceLabel = source === "vimeo" ? "Vimeo" : source === "youtube" ? "YouTube" : source === "kling" ? "Kling AI" : source === "ai" ? "AI Generated" : source === "dropbox" ? "Dropbox" : "";
  const typeLabel = contentType === "reel" ? "Reel" : contentType === "carousel" ? "Carousel" : contentType === "text_post" ? "Text" : contentType === "photo" ? "Photo" : "Video";
  const isPublished = post.status === "published";

  return (
    <div onClick={() => onClick(post)}
      className={`bg-studio-raised border rounded-xl overflow-hidden cursor-pointer hover:border-gold/30 transition-all group ${isPublished ? "border-[#222630] opacity-80" : "border-[#2a2e36]"}`}
      data-testid={`queue-card-${post.id}`}>
      <div className="flex">
        {/* Thumbnail — prefer the processed vertical clip so the card shows the actual cut, not the
            full-length source thumbnail. */}
        <div className="w-32 flex-shrink-0 bg-studio-surface overflow-hidden relative">
          {clipUrl(post) ? (
            <video
              src={`${clipUrl(post)}#t=0.5`}
              className="w-full h-full object-cover"
              muted preload="metadata"
            />
          ) : post.thumbnail_url ? (
            <img src={post.thumbnail_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${post.thumbnail_url}` : post.thumbnail_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center min-h-[100px]"><Film size={20} className="text-zinc-600" /></div>
          )}
          {contentType !== "text_post" && (
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <div className="w-8 h-8 rounded-full bg-black/60 flex items-center justify-center"><Play size={14} className="text-white ml-0.5" /></div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-3.5 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: post.brand?.color_hex || "#71717a" }} />
            <span className="text-xs font-medium text-zinc-300">{post.brand?.name}</span>
            <div className="flex items-center gap-1.5 ml-auto">
              {sourceLabel && <span className="text-[9px] font-mono uppercase text-zinc-500 px-1.5 py-0.5 rounded bg-studio-surface">{sourceLabel}</span>}
              <span className="text-[9px] font-mono uppercase text-zinc-500 px-1.5 py-0.5 rounded bg-studio-surface">{typeLabel}</span>
              {post.post_kind === "full" && (
                <span className="text-[9px] font-mono uppercase text-gold px-1.5 py-0.5 rounded bg-gold/10"
                  title={post.ig_skipped_too_long ? "The whole film — Facebook only (too long for Instagram)" : "The whole film, not the snippet"}>
                  full film{post.full_video_duration ? ` · ${Math.round(post.full_video_duration / 60)}m` : ""}
                </span>
              )}
              {clipUrl(post) && (
                <span className="text-[9px] font-mono uppercase text-teal px-1.5 py-0.5 rounded bg-teal/10" title="AI-edited clip: format · length · extras">
                  {post.clip_format === "smart_crop" ? "9:16 crop" : post.clip_format === "letterbox" ? "letterbox" : "clip"}
                  {post.clip_duration ? ` · ${Math.round(post.clip_duration)}s` : ""}
                  {post.clip_skin ? " · tv" : ""}
                  {post.clip_hook ? " · hook" : ""}
                  {post.clip_subtitle_count > 0 ? " · subs" : ""}
                </span>
              )}
              {post.platforms?.includes("instagram") && <Instagram size={11} className="text-[#c47272]" />}
              {post.platforms?.includes("facebook") && <Facebook size={11} className="text-[#5b8ec4]" />}
            </div>
          </div>
          <p className="text-xs text-zinc-500 font-mono mb-1 truncate">{post.video_title}</p>
          <p className="text-sm text-zinc-300 leading-snug line-clamp-2">{post.caption}</p>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-zinc-500 flex-wrap">
            {cutAt && (
              <span className="flex items-center gap-1" title="When this AI edit was created (re-cutting updates it)">
                <Scissors size={10} />
                <span className="font-mono"><span className="text-zinc-600">cut </span>{fmtDT(cutAt)}</span>
              </span>
            )}
            <span className="flex items-center gap-1" title="When this post will publish, once approved">
              <Clock size={10} />
              <span className="font-mono"><span className="text-zinc-600">posts </span>{slotStale ? "next open slot on approve" : scheduled ? fmtDT(scheduled) : "no date"}</span>
            </span>
            <span className={`status-badge status-${post.status}`}>{post.status?.replace("_", " ")}</span>
            {post.dupe_couple && (
              <span className="text-[9px] font-mono uppercase text-amber-400 px-1.5 py-0.5 rounded bg-amber-400/10" title="This couple appears on more than one queued post (e.g. Feature + Highlights edits). Approving both posts the same wedding twice.">
                same couple ×2
              </span>
            )}
            {isPendingStatus && onApprove && (
              <button onClick={handleCardApprove} disabled={approving}
                className="ml-auto flex items-center gap-1 px-2.5 py-1 rounded-full btn-gold text-[11px] font-medium disabled:opacity-40"
                data-testid={`card-approve-${post.id}`}>
                {approving ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                Approve
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function weekStartOf(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - x.getDay()); // back to Sunday
  return x;
}

function ScheduledRow({ post, onClick }) {
  const dt = post.scheduled_at ? new Date(post.scheduled_at) : null;
  return (
    <div onClick={() => onClick(post)}
      className="flex items-center gap-3 bg-studio-raised border border-[#2a2e36] rounded-xl px-3 py-2 cursor-pointer hover:border-gold/30 transition-all"
      data-testid={`sched-row-${post.id}`}>
      {/* small centre-crop of the clip — no letterbox bars, just the film */}
      <div className="w-14 h-14 rounded-lg overflow-hidden bg-studio-surface flex-shrink-0">
        {clipUrl(post) ? (
          <video src={`${clipUrl(post)}#t=0.5`} muted preload="metadata" className="w-full h-full object-cover" />
        ) : post.thumbnail_url ? (
          <img src={post.thumbnail_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${post.thumbnail_url}` : post.thumbnail_url}
            alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Film size={16} className="text-zinc-600" /></div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: post.brand?.color_hex || "#71717a" }} />
          <span className="text-xs font-medium text-zinc-300 truncate">{post.brand?.name}</span>
          <span className="text-[10px] font-mono text-zinc-600 flex-shrink-0">
            {dt ? dt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}
          </span>
          {post.clip_skin && <span className="text-[9px] font-mono uppercase text-teal px-1.5 py-0.5 rounded bg-teal/10">tv</span>}
          <span className={`status-badge status-${post.status} ml-auto flex-shrink-0`}>{post.status?.replace("_", " ")}</span>
        </div>
        <p className="text-xs text-zinc-400 truncate mt-0.5">{post.caption || post.video_title}</p>
      </div>
    </div>
  );
}

function ScheduledWeeks({ posts, onClick }) {
  const dated = posts.filter((p) => p.scheduled_at)
    .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  const noDate = posts.filter((p) => !p.scheduled_at);
  const weeks = [];
  dated.forEach((p) => {
    const ws = weekStartOf(new Date(p.scheduled_at));
    const wkey = ws.toISOString().slice(0, 10);
    let wk = weeks.find((x) => x.key === wkey);
    if (!wk) { wk = { key: wkey, start: ws, days: [] }; weeks.push(wk); }
    const d = new Date(p.scheduled_at);
    const dkey = d.toDateString();
    let day = wk.days.find((x) => x.key === dkey);
    if (!day) { day = { key: dkey, date: d, posts: [] }; wk.days.push(day); }
    day.posts.push(p);
  });
  const fmtDay = (d) => d.toLocaleDateString([], { month: "short", day: "numeric" });
  const weekRange = (ws) => {
    const we = new Date(ws);
    we.setDate(we.getDate() + 6);
    return `${fmtDay(ws)} – ${fmtDay(we)}`;
  };
  return (
    <div className="space-y-8">
      {weeks.map((wk) => {
        const count = wk.days.reduce((a, d) => a + d.posts.length, 0);
        return (
          <div key={wk.key} data-testid={`week-${wk.key}`}>
            <div className="flex items-center gap-3 mb-3">
              <p className="section-label">// week of {weekRange(wk.start)}</p>
              <span className="text-[10px] font-mono text-zinc-600">{count} post{count === 1 ? "" : "s"}</span>
              <div className="flex-1 h-px bg-[#222630]" />
            </div>
            <div className="space-y-4">
              {wk.days.map((day) => (
                <div key={day.key}>
                  <p className="text-xs text-zinc-300 font-medium mb-1.5">
                    {day.date.toLocaleDateString([], { weekday: "long" })}
                    <span className="text-zinc-600 font-mono text-[10px] ml-2">{fmtDay(day.date)}</span>
                  </p>
                  <div className="space-y-1.5">
                    {day.posts.map((p) => <ScheduledRow key={p.id} post={p} onClick={onClick} />)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {noDate.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-3">
            <p className="section-label">// no date yet</p>
            <div className="flex-1 h-px bg-[#222630]" />
          </div>
          <div className="space-y-1.5">
            {noDate.map((p) => <ScheduledRow key={p.id} post={p} onClick={onClick} />)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ApprovalQueue() {
  const [posts, setPosts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [batchLoading, setBatchLoading] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [confirmReject, setConfirmReject] = useState(null);
  const [publishingCount, setPublishingCount] = useState(0);

  const fetchPosts = async () => {
    setLoading(true);
    // Clear the previous tab's results up front so a slow/failed fetch can never leave one
    // status filter's posts displayed under another tab's header (the spinner covers the gap).
    setPosts([]);
    try {
      let allPosts = [];
      let brandsRes;

      if (statusFilter === "pending") {
        // sort=updated puts freshly re-cut clips first instead of burying them by created_at;
        // revising posts stay visible here with their pulse badge
        const [res1, res2, res3, br] = await Promise.all([
          api.get("/posts", { params: { status: "pending", limit: 50, sort: "updated" } }),
          api.get("/posts", { params: { status: "pending_review", limit: 50, sort: "updated" } }),
          api.get("/posts", { params: { status: "revising", limit: 50, sort: "updated" } }),
          api.get("/brands"),
        ]);
        allPosts = [...res3.data.posts, ...res2.data.posts, ...res1.data.posts];
        brandsRes = br;
      } else {
        const [postsRes, br] = await Promise.all([
          api.get("/posts", { params: { status: statusFilter, limit: 50 } }),
          api.get("/brands"),
        ]);
        allPosts = postsRes.data.posts;
        brandsRes = br;
      }

      // Filter by selected brands (client-side for multi-select)
      if (selectedBrands.length > 0) {
        allPosts = allPosts.filter((p) => selectedBrands.includes(p.brand_id));
      }

      setPosts(allPosts);
      setBrands(brandsRes.data.brands);

      // Check publishing count
      try {
        const pubRes = await api.get("/posts", { params: { status: "publishing", limit: 50 } });
        setPublishingCount(pubRes.data.total || pubRes.data.posts?.length || 0);
      } catch { /* silent */ }
    } catch { setPosts([]); toast.error("Failed to load posts"); }
    setLoading(false);
  };

  useEffect(() => { fetchPosts(); }, [selectedBrands, statusFilter]);

  // While anything is revising: poll, and announce each completion the moment it lands.
  const revisingRef = useRef({});
  useEffect(() => {
    const now = {};
    posts.forEach((p) => { if (p.status === "revising") now[p.id] = p.video_title || "post"; });
    Object.entries(revisingRef.current).forEach(([id, title]) => {
      if (!now[id]) {
        const finished = posts.find((p) => p.id === id);
        if (finished) {
          const last = (finished.revisions || [])[finished.revisions.length - 1] || {};
          if (last.status === "failed") {
            toast.error(`Revision failed: ${title}`, { description: last.summary, duration: 10000 });
          } else {
            toast.success(`Revision done: ${title}`, { description: last.summary, duration: 10000 });
          }
        }
      }
    });
    revisingRef.current = now;
    if (Object.keys(now).length > 0) {
      const t = setInterval(fetchPosts, 20000);
      return () => clearInterval(t);
    }
  }, [posts]);

  const handleRevise = async (postId, notes, screenshotB64, screenshotMime) => {
    try {
      await api.post(`/posts/${postId}/revise`, {
        notes, screenshot_b64: screenshotB64, screenshot_mime: screenshotMime,
      });
      toast.success("On it — the post shows a pulse while the revision runs");
      setSelectedPost(null);
      fetchPosts();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start the revision");
      return false;
    }
  };

  const handleApprove = async (postId) => {
    try {
      const res = await api.post(`/posts/${postId}/approve`);
      const when = res.data?.scheduled_at ? new Date(res.data.scheduled_at).toLocaleString() : null;
      toast.success(
        when
          ? `Approved — publishes ${when}${res.data?.rescheduled ? " (re-booked, old slot had passed)" : ""}`
          : "Post approved and scheduled"
      );
      setSelectedPost(null);
      fetchPosts();
    } catch { toast.error("Failed to approve"); }
  };

  const handleReject = async (postId) => {
    setConfirmReject(postId);
  };

  const confirmRejectPost = async () => {
    try {
      await api.post(`/posts/${confirmReject}/reject`);
      toast.success("Post deleted");
      setConfirmReject(null);
      setSelectedPost(null);
      fetchPosts();
    } catch { toast.error("Failed to delete"); }
  };

  const handlePublishNow = async (postId) => {
    try {
      // Set to publishing status immediately, don't wait for the actual publish
      await api.put(`/posts/${postId}`, { status: "publishing" });
      toast.success("Publishing started — check the Publishing tab");
      setSelectedPost(null);
      fetchPosts();
      // Fire the actual publish in the background
      api.post(`/posts/${postId}/publish-now`).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
  };

  const handleSaveCaption = async (postId, caption) => {
    try {
      // caption_edited stops a later re-cut from overwriting a caption you wrote yourself
      await api.put(`/posts/${postId}`, { caption, caption_edited: true });
      toast.success("Caption saved");
      fetchPosts();
    } catch { toast.error("Failed to save"); }
  };

  const handleRegenerateCaption = async (post) => {
    try {
      const res = await api.post("/ai/generate-caption", {
        brand_id: post.brand_id,
        video_title: post.video_title || "social media post",
      });
      const { caption, options } = res.data;
      await api.put(`/posts/${post.id}`, {
        caption, caption_options: options, caption_angle: options?.[0]?.angle,
      });
      toast.success("New captions generated");
      fetchPosts();
      return res.data;
    } catch { toast.error("Caption generation failed"); return null; }
  };

  const handlePickVariant = async (postId, opt) => {
    try {
      // Picking a generated variant is not a human edit — caption_edited stays unset so
      // a later re-cut can still refresh the wording to match the new footage.
      await api.put(`/posts/${postId}`, { caption: opt.text, caption_angle: opt.angle });
      toast.success(`Caption set (${opt.angle})`);
      fetchPosts();
    } catch { toast.error("Failed to set caption"); }
  };

  const [mineOpen, setMineOpen] = useState(false);
  const [mineStatus, setMineStatus] = useState([]);
  const [mineBrand, setMineBrand] = useState("");
  const [mineCount, setMineCount] = useState(3);
  const [mining, setMining] = useState(false);

  const openMineDialog = async () => {
    setMineOpen(true);
    try {
      const res = await api.get("/content/backlog-status");
      const rows = res.data.brands || [];
      setMineStatus(rows);
      const first = rows.find((r) => r.minable > 0);
      if (first && !mineBrand) setMineBrand(first.brand_id);
    } catch { toast.error("Could not load backlog status"); }
  };

  const handleMine = async () => {
    if (!mineBrand) return;
    setMining(true);
    try {
      const res = await api.post(`/content/mine-backlog?brand=${mineBrand}&count=${mineCount}`);
      const items = res.data.items || [];
      toast.success(`Cutting ${items.length} new edit(s) — they'll appear here as they finish`);
      items.forEach((i) => toast.info(`${i.instant ? "instant" : "full cut"}: ${i.title?.slice(0, 60)}`));
      setMineOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Mining failed");
    }
    setMining(false);
  };

  const handleBatchApprove = async () => {
    setBatchLoading(true);
    try {
      const res = await api.post("/posts/batch-approve");
      toast.success(`Approved ${res.data.approved_count} posts`);
      fetchPosts();
    } catch { toast.error("Batch approve failed"); }
    setBatchLoading(false);
  };

  const handleClearQueue = async () => {
    if (!window.confirm("This will permanently delete ALL pending posts. Are you sure?")) return;
    try {
      const res = await api.post("/posts/clear-queue");
      toast.success(`Cleared ${res.data.deleted_count} posts`);
      fetchPosts();
    } catch { toast.error("Clear failed"); }
  };

  const toggleBrand = (brandId) => {
    setSelectedBrands((prev) => prev.includes(brandId) ? prev.filter((b) => b !== brandId) : [...prev, brandId]);
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="approval-queue">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-display text-3xl font-bold text-zinc-100">Approval Queue</h1>
          <p className="section-label mt-2">// click any post to preview</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => fetchPosts()}
            className="px-3 py-2.5 rounded-xl text-sm flex items-center gap-2 border border-[#2a2e36] text-zinc-400 hover:text-zinc-200 hover:border-zinc-500 transition-colors"
            data-testid="refresh-queue-btn"
            title="Refresh queue">
            <RefreshCw size={14} />
          </button>
          <button onClick={openMineDialog}
            className="px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 border border-teal/30 text-teal hover:bg-teal/10 transition-colors"
            data-testid="mine-backlog-btn"
            title="Cut new reels from the back catalog">
            <Sparkles size={14} /> Create More Edits
          </button>
          {posts.length > 0 && statusFilter === "pending" && (
            <>
              <button onClick={handleClearQueue}
                className="px-4 py-2.5 rounded-xl text-sm flex items-center gap-2 border border-status-red/30 text-status-red hover:bg-status-red/10 transition-colors"
                data-testid="clear-queue-btn">
                <X size={14} /> Clear Queue
              </button>
              <button onClick={handleBatchApprove} disabled={batchLoading}
                className="btn-gold px-4 py-2.5 rounded-xl text-sm flex items-center gap-2"
                data-testid="batch-approve-btn">
                {batchLoading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Approve All
              </button>
            </>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {/* Status filter row */}
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-studio-surface border border-[#2a2e36] rounded-xl overflow-hidden">
            {[
              { key: "pending", label: "Pending" },
              { key: "approved", label: "Scheduled" },
              { key: "published", label: "Published" },
              { key: "failed", label: "Failed" },
            ].map((s) => (
              <button key={s.key} onClick={() => setStatusFilter(s.key)}
                className={`px-3.5 py-2 text-xs transition-colors ${statusFilter === s.key
                  ? "bg-gold/10 text-gold" : "text-zinc-500 hover:text-zinc-300"}`}
                data-testid={`status-filter-${s.key}`}>
                {s.label}
              </button>
            ))}
          </div>

          {/* Publishing indicator */}
          <button onClick={() => setStatusFilter("publishing")}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-xs transition-colors ${
              statusFilter === "publishing" ? "bg-gold/10 text-gold border border-gold/30"
              : publishingCount > 0 ? "bg-gold/5 text-gold border border-gold/20 animate-pulse"
              : "bg-studio-surface text-zinc-500 border border-[#2a2e36]"
            }`}
            data-testid="status-filter-publishing">
            {publishingCount > 0 && <Loader2 size={12} className="animate-spin" />}
            Publishing {publishingCount > 0 && <span className="font-mono">{publishingCount}</span>}
          </button>

          <span className="text-xs text-zinc-500 font-mono ml-auto">{posts.length} {statusFilter === "pending" ? "pending" : "posts"}</span>
        </div>

        {/* Brand pills row */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="section-label mr-1">brands:</span>
          {brands.map((b) => (
            <button key={b.id} onClick={() => toggleBrand(b.id)}
              className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                selectedBrands.includes(b.id)
                  ? "border-gold/40 text-gold bg-gold/5"
                  : "border-[#2a2e36] text-zinc-500 hover:border-zinc-500 hover:text-zinc-400"
              }`}
              data-testid={`brand-pill-${b.id}`}>
              <span className="w-1.5 h-1.5 rounded-full inline-block mr-1.5" style={{ background: b.color_hex }} />
              {b.short_name}
            </button>
          ))}
          {selectedBrands.length > 0 && (
            <button onClick={() => setSelectedBrands([])} className="text-[10px] text-zinc-600 hover:text-zinc-400 ml-1">clear</button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="spinner-gold" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16">
          <Check size={40} className="text-zinc-600 mx-auto mb-3" />
          <p className="text-zinc-400 text-sm">Queue is clear</p>
          <p className="section-label mt-2">// all posts have been reviewed</p>
        </div>
      ) : statusFilter === "approved" ? (
        <ScheduledWeeks posts={posts} onClick={setSelectedPost} />
      ) : (
        <div className="space-y-3 stagger-children">
          {posts.map((post) => (
            <PostCard key={post.id} post={post} onClick={setSelectedPost} onApprove={handleApprove} />
          ))}
        </div>
      )}

      {/* Preview panel — portalled to <body> so position:fixed is relative to the viewport, not the
          page (the queue's animate-fadeIn wrapper keeps a transform that would otherwise trap it). */}
      {selectedPost && createPortal(
        <PreviewPanel
          key={selectedPost.id}
          post={selectedPost}
          onClose={() => setSelectedPost(null)}
          onApprove={handleApprove}
          onReject={handleReject}
          onPublishNow={handlePublishNow}
          onSaveCaption={handleSaveCaption}
          onRegenerateCaption={handleRegenerateCaption}
          onPickVariant={handlePickVariant}
          onRevise={handleRevise}
        />, document.body)}

      {/* Create More Edits — mine the back catalog */}
      <Dialog open={mineOpen} onOpenChange={setMineOpen}>
        <DialogContent className="bg-studio-raised border-[#2a2e36] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-100 font-display">Create More Edits</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-zinc-500 -mt-2">
            Cuts the best un-posted films from the back catalog — never reposts, skips couples already queued.
            Runway = days of posts banked at the brand's cadence.
          </p>
          <div className="space-y-1.5">
            {mineStatus.length === 0 ? (
              <div className="flex justify-center py-4"><Loader2 size={18} className="animate-spin text-zinc-500" /></div>
            ) : mineStatus.map((r) => (
              <button key={r.brand_id} onClick={() => setMineBrand(r.brand_id)} disabled={r.minable === 0}
                className={`w-full flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-colors ${
                  mineBrand === r.brand_id ? "border-gold/50 bg-gold/5" : "border-[#2a2e36] hover:border-[#363b45]"
                } disabled:opacity-40`}
                data-testid={`mine-brand-${r.brand_id}`}>
                <span className="w-2 h-2 rounded-full" style={{ background: r.brand?.color_hex || "#71717a" }} />
                <span className="text-xs text-zinc-200">{r.brand?.name}</span>
                <span className="ml-auto text-[10px] font-mono text-zinc-500">
                  {r.runway_days}d runway · {r.minable} minable{r.instant > 0 ? ` (${r.instant} instant)` : ""}
                </span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-zinc-500">How many:</span>
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} onClick={() => setMineCount(n)}
                className={`w-8 h-8 rounded-lg text-xs font-mono border transition-colors ${
                  mineCount === n ? "border-gold/50 bg-gold/10 text-gold" : "border-[#2a2e36] text-zinc-400 hover:border-[#363b45]"
                }`}>{n}</button>
            ))}
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setMineOpen(false)} className="px-4 py-2 rounded-xl border border-[#363b45] text-zinc-400 text-sm">Cancel</button>
            <button onClick={handleMine} disabled={mining || !mineBrand}
              className="btn-gold px-4 py-2 rounded-xl text-sm flex items-center gap-2 disabled:opacity-40"
              data-testid="mine-start-btn">
              {mining ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
              Start Cutting
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject confirmation */}
      <Dialog open={!!confirmReject} onOpenChange={() => setConfirmReject(null)}>
        <DialogContent className="bg-studio-raised border-[#2a2e36] rounded-2xl max-w-sm" style={{ backgroundColor: "#1a1d21" }}>
          <DialogHeader>
            <DialogTitle className="text-zinc-100 font-display">Delete Post?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-400">This permanently deletes the post. It cannot be undone.</p>
          <DialogFooter className="gap-2">
            <button onClick={() => setConfirmReject(null)} className="px-4 py-2 rounded-xl border border-[#363b45] text-zinc-400 text-sm">Cancel</button>
            <button onClick={confirmRejectPost}
              className="px-4 py-2 rounded-xl bg-status-red text-white text-sm font-medium"
              data-testid="confirm-delete-btn">
              Delete
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
